import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./firebase";
import AdminLogin from "./AdminLogin";
import { signOut } from "firebase/auth";
import AdminPricing from "./AdminPricing";
import AdminRoadPricing from "./AdminRoadPricing";
import CountryFlag from "react-country-flag";

const STATUS_LABELS = {
  new: "Nowe",
  quoted: "Wycenić",
  closed: "Zamknięte",
};

export default function AdminPanel() {
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, loadingUser] = useAuthState(auth);

  // Filtry i sortowanie
  const [filterType, setFilterType] = useState("");
  const [filterPort, setFilterPort] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterLoadFrom, setFilterLoadFrom] = useState("");
  const [filterLoadTo, setFilterLoadTo] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortAsc, setSortAsc] = useState(false);
  const [modalOffer, setModalOffer] = useState(null);
  const [filterArchive, setFilterArchive] = useState(false);

  // Admin notatki (własny komentarz)
  const handleAdminNoteChange = async (id, val) => {
    setOffers(offers =>
      offers.map(o => o.id === id ? { ...o, adminNote: val } : o)
    );
    try {
      await updateDoc(doc(db, "offers", id), { adminNote: val });
    } catch {
      alert("Błąd przy zapisie notatki.");
    }
  };

  // Zmiana statusu
  const handleStatusChange = async (id, status) => {
    setOffers(offers =>
      offers.map(o => o.id === id ? { ...o, status } : o)
    );
    try {
      await updateDoc(doc(db, "offers", id), { status });
    } catch {
      alert("Błąd przy zmianie statusu.");
    }
  };

  // Usuwanie
  const handleDeleteOffer = async (id) => {
    if (!window.confirm("Czy na pewno usunąć tę wycenę?")) return;
    try {
      await deleteDoc(doc(db, "offers", id));
      setOffers(prev => prev.filter(o => o.id !== id));
    } catch (e) {
      alert("Nie udało się usunąć oferty!");
    }
  };

  // Archiwizacja/przywracanie
  const handleArchiveOffer = async (id, archive) => {
    try {
      await updateDoc(doc(db, "offers", id), { archived: archive });
      setOffers(prev =>
        prev.map(o =>
          o.id === id ? { ...o, archived: archive } : o
        )
      );
    } catch {
      alert("Błąd przy zmianie statusu archiwum.");
    }
  };

  // Pobieranie ofert z Firestore
  useEffect(() => {
    if (!user) return;
    if (tab !== "offers") return;
    setLoading(true);
    async function fetchOffers() {
      try {
        const q = query(collection(db, "offers"), orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        const data = [];
        querySnapshot.forEach(doc => {
          // Dodaj archived:false domyślnie jeśli brak
          const d = doc.data();
          data.push({ id: doc.id, ...d, archived: d.archived ?? false });
        });
        setOffers(data);
      } catch (err) {
        alert("Nie udało się pobrać zgłoszeń z Firestore.");
      }
      setLoading(false);
    }
    fetchOffers();
  }, [user, tab]);

  // Przefiltrowane i posortowane dane
  useEffect(() => {
    let arr = [...offers];
    // Filtrowanie
    if (filterType) arr = arr.filter(o => o.transportType === filterType);
    if (filterPort) arr = arr.filter(o => o.origin === filterPort);
    if (filterCity) arr = arr.filter(o => o.destination === filterCity);
    if (filterStatus) arr = arr.filter(o => (o.status || "new") === filterStatus);
    if (!filterArchive) arr = arr.filter(o => !o.archived);
    if (filterArchive) arr = arr.filter(o => !!o.archived);
    if (filterDateFrom) arr = arr.filter(o => o.createdAt >= filterDateFrom);
    if (filterDateTo) arr = arr.filter(o => o.createdAt <= filterDateTo + "T23:59");
    if (filterLoadFrom) arr = arr.filter(o => o.date >= filterLoadFrom);
    if (filterLoadTo) arr = arr.filter(o => o.date <= filterLoadTo);

    // Szukajka
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(o =>
        (o.offerId && o.offerId.toLowerCase().includes(q)) ||
        (o.origin && o.origin.toLowerCase().includes(q)) ||
        (o.destination && o.destination.toLowerCase().includes(q)) ||
        (o.email && o.email.toLowerCase().includes(q)) ||
        (o.comment && o.comment.toLowerCase().includes(q)) ||
        (o.transportType && o.transportType.toLowerCase().includes(q))
      );
    }

    // Sortowanie
    arr.sort((a, b) => {
      let av = a[sortField] || "";
      let bv = b[sortField] || "";
      if (sortField === "totalPrice") {
        av = Number(av);
        bv = Number(bv);
      }
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });

    setFiltered(arr);
  }, [
    offers,
    filterType, filterPort, filterCity, filterStatus,
    filterDateFrom, filterDateTo, filterLoadFrom, filterLoadTo,
    search, sortField, sortAsc, filterArchive
  ]);

  // Wszystkie porty/miasta do filtrów
  const allPorts = [...new Set(offers.map(o => o.origin).filter(Boolean))];
  const allCities = [...new Set(offers.map(o => o.destination).filter(Boolean))];

  if (loadingUser) return <div>Trwa sprawdzanie autoryzacji...</div>;
  if (!user) return <AdminLogin onLogin={() => {}} />;

  // Kolor wiersza wg statusu
  const getRowClass = (status) => {
    if (status === "closed") return "bg-gray-100";
    if (status === "quoted") return "bg-green-50";
    return "bg-yellow-50";
  };

  // Render: flaga + kod ISO + nazwa (jeśli brakuje countryCode nie pokaże flagi)
  const renderCountryFlag = (country, name) =>
    country ? (
      <span className="inline-flex items-center gap-1">
        <CountryFlag countryCode={country} svg style={{ width: "1.3em", height: "1.3em" }} />
        <span className="font-semibold">{country}</span>
        <span>{name}</span>
      </span>
    ) : name;

  return (
    <div className="max-w-full mx-auto p-2">
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-xl font-bold text-blue-700">Panel administracyjny</h1>
        <button
          className="bg-red-500 text-white px-3 py-1 rounded shadow hover:bg-red-600 transition"
          onClick={() => signOut(auth)}
        >Wyloguj</button>
      </div>
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab("offers")}
          className={
            "px-6 py-2 rounded-xl font-semibold shadow transition " +
            (tab === "offers"
              ? "bg-blue-700 text-white shadow-lg scale-105"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300")
          }
        >
          Wyceny
        </button>
        <button
          onClick={() => setTab("pricing")}
          className={
            "px-6 py-2 rounded-xl font-semibold shadow transition " +
            (tab === "pricing"
              ? "bg-blue-700 text-white shadow-lg scale-105"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300")
          }
        >
          Cennik morski
        </button>
        <button
          onClick={() => setTab("road")}
          className={
            "px-6 py-2 rounded-xl font-semibold shadow transition " +
            (tab === "road"
              ? "bg-blue-700 text-white shadow-lg scale-105"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300")
          }
        >
          Dostawy drogowe
        </button>
      </div>

      {tab === "offers" && (
        <>
          {/* Filtry i search */}
          <div className="flex flex-wrap gap-2 mb-2 items-center text-xs">
            <select className="input w-auto" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">Typ</option>
              <option value="FCL">FCL</option>
              <option value="LCL">LCL</option>
            </select>
            <select className="input w-auto" value={filterPort} onChange={e => setFilterPort(e.target.value)}>
              <option value="">Port</option>
              {allPorts.map(port => <option key={port}>{port}</option>)}
            </select>
            <select className="input w-auto" value={filterCity} onChange={e => setFilterCity(e.target.value)}>
              <option value="">Miasto</option>
              {allCities.map(city => <option key={city}>{city}</option>)}
            </select>
            <select className="input w-auto" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Status</option>
              <option value="new">Nowe</option>
              <option value="quoted">Wycenić</option>
              <option value="closed">Zamknięte</option>
            </select>
            <input className="input w-32" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} placeholder="Utw. od" />
            <input className="input w-32" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} placeholder="Utw. do" />
            <input className="input w-32" type="date" value={filterLoadFrom} onChange={e => setFilterLoadFrom(e.target.value)} placeholder="Załad. od" />
            <input className="input w-32" type="date" value={filterLoadTo} onChange={e => setFilterLoadTo(e.target.value)} placeholder="Załad. do" />
            <input className="input w-40" placeholder="Szukaj..." value={search} onChange={e => setSearch(e.target.value)} />
            <label className="flex items-center gap-1 ml-2">
              <input
                type="checkbox"
                checked={filterArchive}
                onChange={e => setFilterArchive(e.target.checked)}
              />
              Archiwum
            </label>
          </div>
          {/* Tabela */}
          <div className="overflow-x-auto" style={{ maxWidth: "100vw" }}>
            <table className="w-full text-xs border" style={{ minWidth: 950, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 58 }} /> {/* Nr */}
                <col style={{ width: 62 }} /> {/* Data utw. */}
                <col style={{ width: 62 }} /> {/* Zał. */}
                <col style={{ width: 130 }} /> {/* Port załadunku */}
                <col style={{ width: 130 }} /> {/* Miasto docelowe */}
                <col style={{ width: 110 }} /> {/* Transport/ładunek */}
                <col style={{ width: 70 }} /> {/* Fracht */}
                <col style={{ width: 70 }} /> {/* THC/adm */}
                <col style={{ width: 70 }} /> {/* Dost. drogowa */}
                <col style={{ width: 80 }} /> {/* Total */}
                <col style={{ width: 74 }} /> {/* Status */}
                <col style={{ width: 140 }} /> {/* Komentarz */}
                <col style={{ width: 105 }} /> {/* Notatka */}
                <col style={{ width: 98 }} /> {/* Archiwum/Usuń */}
              </colgroup>
              <thead>
                <tr className="bg-blue-50 text-[13px]">
                  <th className="border p-1 cursor-pointer" onClick={() => { setSortField("offerId"); setSortAsc(s => !s); }}>Nr</th>
                  <th className="border p-1 cursor-pointer" onClick={() => { setSortField("createdAt"); setSortAsc(s => !s); }}>Utw.</th>
                  <th className="border p-1 cursor-pointer" onClick={() => { setSortField("date"); setSortAsc(s => !s); }}>Zał.</th>
                  <th className="border p-1">Port załadunku</th>
                  <th className="border p-1">Miasto docelowe</th>
                  <th className="border p-1">Transport/ładunek</th>
                  <th className="border p-1">Fracht</th>
                  <th className="border p-1">THC/adm.</th>
                  <th className="border p-1">Dost. drogowa</th>
                  <th className="border p-1 cursor-pointer" onClick={() => { setSortField("totalPrice"); setSortAsc(s => !s); }}>Total</th>
                  <th className="border p-1">Status</th>
                  <th className="border p-1">Komentarz</th>
                  <th className="border p-1">Notatka</th>
                  <th className="border p-1">Archiwum/Usuń</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className={getRowClass(o.status || "new") + " hover:bg-blue-100 transition"}>
                    <td className="border p-1 font-bold text-blue-700 underline cursor-pointer" onClick={() => setModalOffer(o)}>{o.offerId}</td>
                    <td className="border p-1">{o.createdAt?.slice(0, 10)}</td>
                    <td className="border p-1">{o.date}</td>
                    <td className="border p-1 whitespace-nowrap">{renderCountryFlag(o.originCountry, o.origin)}</td>
                    <td className="border p-1 whitespace-nowrap">{renderCountryFlag(o.destCountry, o.destination)}</td>
                    <td className="border p-1 font-semibold text-gray-700">
                      {o.transportType}{" "}
                      {(o.totalVolume || o.fclWeight || o.lclTotalWeight) &&
                        <span>
                          {Number(o.totalVolume || 0).toFixed(2)}m³/
                          {Number(o.fclWeight || o.lclTotalWeight || 0)}kg
                        </span>
                      }
                    </td>
                    <td className="border p-1">{o.seaPrice}</td>
                    <td className="border p-1">
                      {o.thc
                        ? <>THC: {o.thc}</>
                        : o.adminFee
                          ? <>Opł. adm.: {o.adminFee}</>
                          : "—"
                      }
                    </td>
                    <td className="border p-1">{o.roadPrice || "—"}</td>
                    <td className="border p-1 font-bold">{o.totalPrice}</td>
                    <td className="border p-1">
                      <select
                        className="input w-auto"
                        value={o.status || "new"}
                        onChange={e => handleStatusChange(o.id, e.target.value)}
                      >
                        <option value="new">Nowe</option>
                        <option value="quoted">Wycenić</option>
                        <option value="closed">Zamknięte</option>
                      </select>
                    </td>
                    <td className="border p-1 max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis" title={o.comment}>
                      {o.comment?.length > 55 ? o.comment.slice(0, 52) + "…" : o.comment}
                    </td>
                    <td className="border p-1">
                      <input
                        className="input w-24"
                        type="text"
                        value={o.adminNote || ""}
                        placeholder="—"
                        onChange={e => handleAdminNoteChange(o.id, e.target.value)}
                      />
                    </td>
                    <td className="border p-1 text-center">
                      {!filterArchive ? (
                        <button
                          className="bg-yellow-400 text-gray-700 px-2 py-1 rounded hover:bg-yellow-500 mr-1"
                          onClick={() => handleArchiveOffer(o.id, true)}
                        >Archiwizuj</button>
                      ) : (
                        <button
                          className="bg-green-400 text-gray-800 px-2 py-1 rounded hover:bg-green-500 mr-1"
                          onClick={() => handleArchiveOffer(o.id, false)}
                        >Przywróć</button>
                      )}
                      <button
                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-700"
                        onClick={() => handleDeleteOffer(o.id)}
                      >Usuń</button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={14} className="text-center text-gray-500 py-8">Brak wycen do wyświetlenia.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Podgląd szczegółowy (modal) */}
          {modalOffer && (
            <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4" onClick={() => setModalOffer(null)}>
              <div className="bg-white rounded-2xl shadow-lg p-6 max-w-2xl w-full relative" onClick={e => e.stopPropagation()}>
                <h2 className="text-lg font-bold text-blue-700 mb-2">Szczegóły wyceny {modalOffer.offerId}</h2>
                <table className="w-full text-xs">
                  <tbody>
                    <tr><td className="font-bold pr-2">Port załadunku:</td><td>{renderCountryFlag(modalOffer.originCountry, modalOffer.origin)}</td></tr>
                    <tr><td className="font-bold pr-2">Miasto docelowe:</td><td>{renderCountryFlag(modalOffer.destCountry, modalOffer.destination)}</td></tr>
                    <tr><td className="font-bold pr-2">Data załadunku:</td><td>{modalOffer.date}</td></tr>
                    <tr><td className="font-bold pr-2">Transport/ładunek:</td>
                      <td>
                        {modalOffer.transportType}{" "}
                        {(modalOffer.totalVolume || modalOffer.fclWeight || modalOffer.lclTotalWeight) &&
                          <span>
                            {Number(modalOffer.totalVolume || 0).toFixed(2)}m³/
                            {Number(modalOffer.fclWeight || modalOffer.lclTotalWeight || 0)}kg
                          </span>
                        }
                      </td>
                    </tr>
                    <tr><td className="font-bold pr-2">Fracht morski:</td><td>{modalOffer.seaPrice}</td></tr>
                    <tr><td className="font-bold pr-2">THC/Opł. adm.:</td>
                      <td>
                        {modalOffer.thc
                          ? <>THC: {modalOffer.thc}</>
                          : modalOffer.adminFee
                            ? <>Opł. adm.: {modalOffer.adminFee}</>
                            : "—"
                        }
                      </td>
                    </tr>
                    <tr><td className="font-bold pr-2">Dostawa drogowa:</td><td>{modalOffer.roadPrice}</td></tr>
                    <tr><td className="font-bold pr-2">Total:</td><td>{modalOffer.totalPrice}</td></tr>
                    <tr><td className="font-bold pr-2">E-mail:</td><td>{modalOffer.email}</td></tr>
                    <tr><td className="font-bold pr-2">Komentarz klienta:</td><td>{modalOffer.comment}</td></tr>
                    <tr><td className="font-bold pr-2">Notatka admina:</td><td>{modalOffer.adminNote}</td></tr>
                    <tr><td className="font-bold pr-2">Data utworzenia:</td><td>{modalOffer.createdAt}</td></tr>
                  </tbody>
                </table>
                <button className="btn mt-4" onClick={() => setModalOffer(null)}>Zamknij</button>
              </div>
            </div>
          )}
        </>
      )}
      {tab === "pricing" && <AdminPricing />}
      {tab === "road" && <AdminRoadPricing />}
      <style>{`
      .input {
        @apply px-2 py-1 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 transition;
      }
      .btn {
        @apply bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition;
      }
      .btn-secondary {
        @apply bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition;
      }
      `}</style>
    </div>
  );
}
